import { languages } from '@codemirror/language-data';
import { isMarkdownFile } from './fileType';

/**
 * A selectable language for the `/lang` command and the tab "Change language"
 * menu. `key` is a stable lowercased identifier (the source of truth for an
 * `OpenDoc.langOverride`); `label` is the human name; `primaryExt` is the
 * extension that drives the effective filename (and therefore syntax colors,
 * available views, outline, palette, and the tab icon).
 */
export interface LanguageChoice {
  key: string;
  label: string;
  primaryExt: string;
}

/** Explicit Plain Text choice. Prepended so it's the obvious top option, and so
 *  an untitled buffer's default (`txt`) maps to a real entry. */
const PLAIN_TEXT_CHOICE: LanguageChoice = {
  key: 'plaintext',
  label: 'Plain Text',
  primaryExt: 'txt',
};

/**
 * The full language catalogue: Plain Text first, then every
 * `@codemirror/language-data` language that has at least one extension (keyed by
 * its lowercased name, labelled by its real name, with its first extension as
 * the primary). Markdown is guaranteed present (it ships in language-data, but
 * we add a fallback in case that ever changes).
 */
export const LANGUAGE_CHOICES: LanguageChoice[] = buildLanguageChoices();

function buildLanguageChoices(): LanguageChoice[] {
  const out: LanguageChoice[] = [PLAIN_TEXT_CHOICE];
  const seen = new Set<string>([PLAIN_TEXT_CHOICE.key]);
  for (const desc of languages) {
    const ext = desc.extensions[0];
    if (!ext) continue; // skip languages with no file extension (e.g. dialect SQLs)
    const key = desc.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, label: desc.name, primaryExt: ext });
  }
  if (!seen.has('markdown')) {
    out.push({ key: 'markdown', label: 'Markdown', primaryExt: 'md' });
  }
  return out;
}

const CHOICE_BY_KEY: ReadonlyMap<string, LanguageChoice> = new Map(
  LANGUAGE_CHOICES.map((c) => [c.key, c]),
);

/** Look up a choice by key, or undefined for an unknown key. */
export function languageChoice(key: string): LanguageChoice | undefined {
  return CHOICE_BY_KEY.get(key);
}

/**
 * All file extensions associated with a language key (e.g. typescript ->
 * `['ts', 'tsx', 'mts', 'cts']`), used to build the save dialog's filter. Plain
 * Text returns `['txt']`; an unknown key returns `[]`.
 */
export function extensionsForChoice(key: string): string[] {
  if (key === PLAIN_TEXT_CHOICE.key) return [PLAIN_TEXT_CHOICE.primaryExt];
  const desc = languages.find((d) => d.name.toLowerCase() === key);
  return desc ? [...desc.extensions] : [];
}

/**
 * Primary extension for a language key. Unknown keys fall back to `txt` (Plain
 * Text) so an override never produces a name without an extension.
 */
export function primaryExtForChoice(key: string): string {
  return CHOICE_BY_KEY.get(key)?.primaryExt ?? 'txt';
}

/** The filename stem (everything before the last dot), or the whole name when
 *  it has no extension. */
function stemOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Minimal doc shape the resolver reads — a subset of `OpenDoc`. */
interface DocLike {
  ref: { name: string } | null;
  langOverride?: string;
  kind?: 'text' | 'image';
}

/**
 * The filename the document should behave as, given its language override and
 * real ref. Resolution order:
 *   1. `langOverride` set -> `<stem>.<primaryExt(override)>` (stem from the real
 *      name, else `untitled`).
 *   2. a real `ref.name` -> returned verbatim (the real extension drives).
 *   3. untitled with no override -> `untitled.txt` (Plain Text default).
 */
export function effectiveFileName(doc: Pick<DocLike, 'ref' | 'langOverride'>): string | null {
  if (doc.langOverride) {
    const stem = doc.ref?.name ? stemOf(doc.ref.name) : 'untitled';
    return `${stem}.${primaryExtForChoice(doc.langOverride)}`;
  }
  if (doc.ref?.name) return doc.ref.name;
  return 'untitled.txt';
}

/** The resolved language facts a document's UI should follow. */
export interface ResolvedDocLanguage {
  effectiveName: string | null;
  isMarkdown: boolean;
  primaryExt: string | null;
}

/** Lowercase extension (no dot) of `name`, or '' when there's none. */
function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/**
 * The `LANGUAGE_CHOICES` key a document currently behaves as: its `langOverride`
 * when set, else the language matched from its effective extension, else
 * `plaintext`. Used by the save flow to pick filters and the default filename.
 */
export function langKeyForDoc(doc: Pick<DocLike, 'ref' | 'langOverride'>): string {
  if (doc.langOverride) return doc.langOverride;
  const name = effectiveFileName(doc);
  if (!name) return 'plaintext';
  const ext = extOf(name);
  if (ext === '' || ext === 'txt' || ext === 'text') return 'plaintext';
  for (const desc of languages) {
    if (desc.extensions.includes(ext)) return desc.name.toLowerCase();
  }
  return 'plaintext';
}

/**
 * Resolve every filename-derived language fact for a document from its effective
 * filename. `image` docs are never markdown (they open in a read-only viewer).
 */
export function resolveDocLanguage(
  doc: Pick<DocLike, 'ref' | 'langOverride' | 'kind'>,
): ResolvedDocLanguage {
  const effectiveName = effectiveFileName(doc);
  const isMarkdown =
    doc.kind !== 'image' && effectiveName != null && isMarkdownFile(effectiveName);
  const primaryExt = effectiveName ? extOf(effectiveName) || null : null;
  return {
    effectiveName,
    isMarkdown,
    primaryExt,
  };
}
